import React, { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { siteConfig } from "../data/mock";
import { MapPin, ArrowRight } from "lucide-react";

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.15, ...options }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
};

export default function ContactPage() {
  const [heroRef, heroInView] = useInView();
  const [bodyRef, bodyInView] = useInView();

  return (
    <main data-testid="contact-page">
      <section
        ref={heroRef}
        className="pt-32 md:pt-44 pb-20 md:pb-28 px-6 md:px-12 bg-[#FAFAF7]"
      >
        <div className="max-w-[1400px] mx-auto">
          <span
            className={`font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block transition-all duration-1000 ${
              heroInView ? "opacity-100" : "opacity-0"
            }`}
          >
            Contact
          </span>
          <h1
            className={`font-display text-5xl md:text-6xl lg:text-7xl text-stone-900 leading-[1.05] mb-8 max-w-3xl transition-all duration-1000 delay-200 ${
              heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Let's start a conversation.
          </h1>
          <p
            className={`font-body text-stone-500 text-lg leading-relaxed max-w-2xl transition-all duration-1000 delay-400 ${
              heroInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Every great home begins with a conversation. Tell us about your
            vision through our design questionnaire — it's the fastest way for
            us to understand who you are and how you want to live.
          </p>
        </div>
      </section>

      <section ref={bodyRef} className="pb-28 md:pb-40 px-6 md:px-12 bg-[#FAFAF7]">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Address card */}
            <div
              className={`lg:col-span-5 transition-all duration-1000 ${
                bodyInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
              data-testid="contact-address-block"
            >
              <h3 className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6">
                Studio
              </h3>
              <div className="flex items-start gap-5 pb-8 border-b border-stone-200">
                <div className="w-11 h-11 flex items-center justify-center bg-stone-900 shrink-0">
                  <MapPin size={18} strokeWidth={1.5} className="text-white" />
                </div>
                <div>
                  <p className="font-display text-2xl md:text-3xl text-stone-900 leading-tight">
                    5249 N Park Pl NE
                  </p>
                  <p className="font-body text-stone-500 text-base mt-1">
                    Cedar Rapids, IA 52402
                  </p>
                </div>
              </div>

              <p className="font-body text-stone-500 text-sm leading-relaxed mt-8 max-w-md">
                We work by appointment only. The questionnaire helps us
                understand your project so the first conversation is meaningful
                — not generic.
              </p>
            </div>

            {/* Journey CTA card */}
            <div
              className={`lg:col-span-7 transition-all duration-1000 delay-200 ${
                bodyInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"
              }`}
              data-testid="contact-journey-cta"
            >
              <div className="relative bg-stone-900 text-white p-10 md:p-14 overflow-hidden">
                {/* Accent corner */}
                <div className="absolute top-0 right-0 w-1 h-24 bg-[#B8977E]" />
                <div className="absolute top-0 right-0 w-24 h-1 bg-[#B8977E]" />

                <span className="font-body text-[11px] tracking-[0.3em] uppercase text-[#B8977E] mb-6 block">
                  Begin Here
                </span>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] mb-6">
                  Take our design questionnaire.
                </h2>
                <p className="font-body text-white/60 text-base md:text-lg leading-relaxed mb-10 max-w-lg">
                  Nine thoughtful questions that help us understand your
                  vision, your land, your lifestyle, and your timeline. Takes
                  about five minutes — and we'll respond personally within
                  one business day.
                </p>

                <Link
                  to="/catapult/journey"
                  data-testid="contact-start-journey-btn"
                  className="group inline-flex items-center gap-4 bg-white text-stone-900 px-8 py-5 font-body text-sm tracking-[0.15em] uppercase hover:bg-[#B8977E] hover:text-white transition-colors duration-300"
                >
                  Start your journey
                  <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform duration-300" />
                </Link>

                <div className="mt-12 pt-8 border-t border-white/10 flex flex-wrap gap-x-10 gap-y-4 text-white/40 font-body text-[11px] tracking-[0.2em] uppercase">
                  <span>9 Questions</span>
                  <span>~5 Minutes</span>
                  <span>Replies within 24 hours</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
